/** Signing in, from the browser's side.
 *
 * The session lives in an **httpOnly cookie** (ADR-0008), which is the shape that matters here: this
 * module never sees a token, never stores one, and has nothing to put in `localStorage`. The browser
 * attaches the cookie, **but only if the request asks it to**: in production the API is on a sibling
 * hostname (ADR-0049), which is cross-origin, and `fetch` sends no cookie cross-origin unless
 * `credentials` is `"include"`. `client.ts` sets that once on the client, so nothing here repeats it.
 *
 * So "am I signed in?" is answered by asking the server — `GET /auth/me` — and not by reading a
 * client-side flag. That is slower by one request on load and it is the only version that cannot be
 * wrong: a flag in memory says what the client last believed, and a session that expired ten minutes
 * ago is exactly the case where the two disagree.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { clearAllDrafts } from "../lib/drafts";
import { refusalFrom } from "./refusal";
import { clearSessionEnded, markSessionEnded } from "./sessionEnded";

export const authKeys = {
  me: () => ["auth", "me"] as const,
};

export interface Me {
  user_id: string;
  org_id: string;
  role: string;
  email: string;
  display_name: string;
  mfa_enrolled: boolean;
  /** What narrows this person's reads, if anything: an Auditor's engagement over a `period`, or over
   * particular `companies`. Absent for everybody else. The audit ledger refuses an engaged reader by
   * design, so the screen reads this instead of asking (`Ledger.tsx`). */
  engagement?: "period" | "companies" | null;
  /** **What the engagement is** - an Auditor's own terms (Q-03): the window they may read and the last
   * day their access runs. Null for every other role; null fields for an Auditor with no window and
   * no end. The companies are not here: they are what `GET /entities` returns, clamped by the same
   * row-level policies as everything else. */
  engagement_terms?: {
    period_start?: string | null;
    period_end?: string | null;
    access_ends_on?: string | null;
  } | null;
  /** The reader's own date and clock preferences in this workspace (they default from the
   * deployment's market settings). Read by `ReaderLocaleScope`; absent from an older server. */
  locale?: "en-GB" | "en-US" | null;
  time_zone?: string | null;
}

/** The signed-in person, or `null`.
 *
 * A 401 is **not** an error here — it is the answer "nobody". Treating it as a failure would make
 * React Query retry it three times on every page load for a visitor who is simply not signed in, and
 * would put a red error state where a sign-in form belongs.
 */
export function useMe() {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async (): Promise<Me | null> => {
      const { data, response } = await api.GET("/api/v1/auth/me", {});
      if (response.status === 401) {
        // **Signed in a moment ago, nobody now** — the focus re-check below finding that the
        // session ended while the tab was away. That is an expiry the reader should be told about;
        // a first visit (nothing cached) or a sign-out (cached `null`) is not. The 401 handler in
        // `client.ts` ignores `/auth/` on purpose, so this is the only place that can see it.
        if (queryClient.getQueryData(authKeys.me())) markSessionEnded();
        return null;
      }
      if (!data) throw new Error("could not read the current session");
      return data;
    },
    retry: false,
    // Re-checked on focus, because a session expires on the server's clock and the tab has no way
    // to know. Without this a left-open tab shows a working UI whose every action 401s.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export type LoginResult =
  | { status: "signed_in" }
  | { status: "mfa_required"; enrolmentRequired: boolean }
  | { status: "refused" }
  /** **429: this network has failed too often (gate 12).** Not a refused password, and the remedy
   * is different - wait - so it is its own result. Telling somebody their password is wrong when
   * the truth is "stop for a few minutes" has them retry, which is the thing being limited. */
  | { status: "throttled" };

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }): Promise<LoginResult> => {
      // No `org_id`: the server resolves which organisation from the person's memberships. See the
      // sign-in screen on why a form cannot offer that choice before authentication.
      const { data, response } = await api.POST("/api/v1/auth/login", {
        body: { email: input.email, password: input.password },
      });
      // A refusal is a result, not an exception. Every refusal is the same 401 by design, and the
      // form shows one message — so there is nothing for an error boundary to add.
      if (response.status === 401) return { status: "refused" };
      if (response.status === 429) return { status: "throttled" };
      if (!data) return { status: "refused" };
      if (data.mfa_required) {
        return { status: "mfa_required", enrolmentRequired: data.mfa_enrolment_required };
      }
      return { status: "signed_in" };
    },
    onSuccess: async (result) => {
      if (result.status === "signed_in") {
        await queryClient.invalidateQueries({ queryKey: authKeys.me() });
      }
    },
  });
}

export function useCompleteMfa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string): Promise<boolean | "throttled"> => {
      const { response } = await api.POST("/api/v1/auth/mfa", { body: { code } });
      // 429 is its own answer for the reason `LoginResult` gives: "that code was not accepted"
      // invites another guess from a network that has been told to stop guessing.
      if (response.status === 429) return "throttled";
      return response.ok;
    },
    onSuccess: async (ok) => {
      if (ok === true) await queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
  });
}

export interface Enrolment {
  secret: string;
  provisioning_uri: string;
  recovery_codes: string[];
}

/** Create a TOTP secret and recovery codes.
 *
 * The response is shown **once** and never cached: `useMutation`, not `useQuery`, so React Query has
 * nowhere to keep it and a re-render cannot resurrect it. Recovery codes in a query cache would sit
 * in memory for the rest of the session and appear in any devtools panel someone opened.
 */
export function useEnrolMfa() {
  return useMutation({
    mutationFn: async (): Promise<Enrolment> => {
      const { data, error, response } = await api.POST("/api/v1/auth/mfa/enrol", {});
      if (data) return data;
      // **`new Error("enrolment failed")` was exactly the defect `refusal.ts` was written to stop**
      // — *"loses the one fact a screen has to branch on, and every refusal then renders as the
      // same paragraph"* — sitting one import away from the class that fixes it. The paragraph it
      // rendered was "try again, or reload this page", and the refusal it rendered it for was a
      // 401 from a `PENDING_MFA` session that had expired, where neither is possible.
      throw refusalFrom(response, error, "The setup key could not be prepared.");
    },
    gcTime: 0,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.POST("/api/v1/auth/logout", {});
    },
    onSuccess: () => {
      // Everything, not just the session. A cache left populated after sign-out would render one
      // organisation's figures to whoever signs in next on a shared machine.
      //
      // **Say "nobody" first, on the query the mounted screen is already watching** (found
      // 2026-10-30 by driving a real browser). The order used to be `clear()` and then
      // `setQueryData`, and `clear()` removes the very query `RequireSession`'s observer holds: the
      // value written afterwards went to a *new* query nobody observed, so nothing re-rendered and
      // the signed-out person kept looking at the signed-in screen, indefinitely and through a
      // window focus. Writing "nobody" synchronously (rather than invalidating and waiting for a
      // refetch, which left the screen up until the next navigation) is still the point; it just
      // has to reach the observer. Then everything *else* goes, so no figure outlives the session -
      // **after** the screen holding those queries has unmounted, because a still-mounted observer
      // re-creates a query that was removed under it (the mutation's own state change re-renders it
      // first). One macrotask is the earliest point at which the render that drops them has run.
      // Half-finished decisions kept in this browser (EXP-35) belong to the person who left.
      clearAllDrafts();
      queryClient.setQueryData(authKeys.me(), null);
      const me = JSON.stringify(authKeys.me());
      setTimeout(() => {
        queryClient.removeQueries({ predicate: (query) => JSON.stringify(query.queryKey) !== me });
      }, 0);
      // Leaving on purpose is not being thrown out, so the sign-in form that follows must not say
      // the session ended — whatever a straggling 401 raised before the sign-out landed.
      clearSessionEnded();
    },
  });
}
